/**
 * ackVerify — the POSITIVE-CONFIRMATION contract for the durable outbox.
 *
 * FR-FE-IRONCLAD-TELEMETRY-OUTBOX. An outbox entry is a rep's only copy of a
 * field write until the server has provably taken it. So the rule for removing it
 * is not "the call returned" and not "we got a 2xx" — it is "the server confirmed a
 * durable id". This module is the ONE place that predicate lives, so the online
 * submit path (useOfflineMutation) and the replay path (queueFlush.pruneQueue)
 * cannot drift about what "confirmed" means.
 *
 * Pure by construction: it imports nothing from the offline layer, so it can be
 * consumed by `offlineQueueTypes` (the handler SIGNATURE) and by `queueFlush`
 * (the prune decision) without any import cycle.
 *
 * A `QueueHandler` returns `QueueAck | void`:
 *   - `{ verified: true, serverId? }` — the server CONFIRMED the write. Prune.
 *   - `void` — a legacy handler that cannot yet report an ack. Its completion is
 *     the only signal we have, so it is trusted (backward-compatible with every
 *     handler that predates this contract). Prune.
 *   - a DEFINED value that is NOT a verified ack — a bare 2xx with no confirmation.
 *     `isVerifiedAck` returns false, and the caller RETAINS the entry: a reply that
 *     does not confirm a durable id must never delete the rep's only copy.
 */

/**
 * A handler's proof that the server durably accepted the replayed write.
 *
 * `verified` is a literal `true` (never a boolean the caller can accidentally leave
 * false and still satisfy the shape). `serverId` is the durable row id the server
 * assigned, carried for forensics/idempotency; it is optional because some confirmed
 * writes have no single row id, but its PRESENCE is what a caller asserts to prove
 * the write landed.
 */
export interface QueueAck {
  verified: true
  serverId?: string
}

/**
 * Is `result` a verified ack — positive proof the server took the write?
 *
 * TRUE only for `{ verified: true }`. Deliberately NOT true for `undefined`/`void`
 * (that is the legacy "handler completed" signal, handled distinctly by the caller)
 * and NOT true for any other object shape (a bare 2xx that confirmed nothing). This
 * is the anti-fallacy heart of the outbox: "the handler resolved" is not "the server
 * confirmed", and only the latter earns a delete.
 */
export function isVerifiedAck(result: unknown): result is QueueAck {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as { verified?: unknown }).verified === true
  )
}
